import type { AsyncResult } from "operation-result.js";
import type { Disposable, Rx } from "subtree.js/core";
import { failedLoad, idleLoad, type LoadStatus, loaded, loading } from "./load-status";

/** Long enough that a word is one request rather than six. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface LoaderOptions<A, T> {
  /** Mirrors the request's life. The page's model owns it, so the view reads it as any other field. */
  status: Rx<LoadStatus>;
  fetch: (args: A) => AsyncResult<T, Error>;
  /** Receives an answer that is still current: nothing newer was started, reset or disposed meanwhile. */
  loaded: (value: T, args: A) => void;
  /** The quiet time `loadDebounced` waits for. */
  debounceMs?: number;
}

/**
 * One request whose latest run wins. A search box, a filtered table, a detail
 * keyed by an id: whenever the input can change faster than the answer comes
 * back, the answer to an older input must be dropped, and this is the one
 * place that rule lives. `loadDebounced` also waits for typing to settle.
 */
export class Loader<A, T> implements Disposable {
  #seq = 0;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #disposed = false;

  constructor(readonly options: LoaderOptions<A, T>) {}

  /** Runs now, superseding anything pending or in flight. */
  load(args: A): Promise<void> {
    this.#cancelTimer();
    return this.#run(args);
  }

  /** Runs once `debounceMs` passes with no newer call. */
  loadDebounced(args: A): void {
    this.#cancelTimer();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      void this.#run(args);
    }, this.options.debounceMs ?? SEARCH_DEBOUNCE_MS);
  }

  /** Back to idle. Anything pending or in flight is dropped. */
  reset(): void {
    this.#cancelTimer();
    this.#seq++;
    this.options.status.value = idleLoad;
  }

  dispose(): void {
    this.#disposed = true;
    this.#cancelTimer();
    this.#seq++;
  }

  async #run(args: A): Promise<void> {
    if (this.#disposed) return;
    const seq = ++this.#seq;
    this.options.status.value = loading;
    const r = await this.options.fetch(args);
    if (seq !== this.#seq) return;
    if (!r.ok) {
      this.options.status.value = failedLoad(r.errors);
      return;
    }
    this.options.loaded(r.value, args);
    this.options.status.value = loaded;
  }

  #cancelTimer(): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
  }
}
